import { Text, Flexbox, DarkTooltip } from '@morressier/ts';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faEyeSlash,
  faTrash,
  faClose,
  faEnvelope,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import { format, differenceInHours } from 'date-fns';
import AffiliationItemStatus from './AffiliationItemStatus';
import { KeyedMutator } from 'swr';
import { updatePartialProfileGeneralInfo } from 'clients/internal/profileService';
import { IProfile } from 'typings/profile';
import { IAffiliationResponse } from 'typings/organization';
import { deleteAffiliation } from './hooks';
import { Modal, ExitModal, LinkButton } from 'components/ProfileUIComponents';
import { FormContainer, Loader } from './EmailAffiliation/style';
import EmailValidationView from './EmailAffiliation/steps/EmailValidationView';
import { resendAffiliationVerificationEmail } from './hooks';
import { getInstitutionTypeText, sentryCaptureException } from 'utils/utils';
import { useFeatureFlagContext } from 'context/FeatureFlagProvider';
import { FEATURE_FLAGS, MESSAGES } from 'utils/constants';
import { SecondaryText } from '../styles';
import { logEvent } from 'utils/eventTracking';

const Title = styled(Text)`
  font-size: var(--fontSizes-m);
  line-height: var(--lineHeights-m);
`;

const HiddenText = styled(Text)`
  color: var(--color-mono-60);
`;

interface IAffiliationItemProps {
  refetchAffiliations: KeyedMutator<IAffiliationResponse>;
  id: string;
  title: string;
  organization: string;
  department: string;
  location: string;
  startDate: string;
  endDate: string | null;
  createdAt: string;
  email: string;
  organizationId?: string;
  organizationType?: string;
  ringgold?: string;
  validatedAt: string | null;
  isSameUser?: boolean;
  type: string;
  isEmailDomainMatchesOrgDomain?: boolean;
  onEditClick: (key: string) => void;
  affiliationType: 'RINGGOLD' | 'USER_PROVIDED';
  mutateProfile: KeyedMutator<IProfile>;
  isPrimaryAffiliation: boolean;
  userId: string;
}

const AffiliationItem = (props: IAffiliationItemProps) => {
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editAffiliationDisabled, setEditAffiliationDisabled] = useState(false);
  const features = useFeatureFlagContext();
  useEffect(() => {
    if (features[FEATURE_FLAGS.DISABLE_AFFILIATION_EDITING] !== undefined) {
      setEditAffiliationDisabled(
        !!features[FEATURE_FLAGS.DISABLE_AFFILIATION_EDITING]
      );
    }
  }, [features]);

  const {
    id,
    title,
    organization,
    department,
    location,
    startDate,
    endDate,
    organizationId,
    organizationType,
    ringgold,
    validatedAt,
    isSameUser,
    createdAt,
    email,
    type,
    refetchAffiliations,
    affiliationType,
    isEmailDomainMatchesOrgDomain,
    isPrimaryAffiliation,
    userId,
    mutateProfile
  } = props;

  const onClick = () => {
    props.onEditClick(id);
  };
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const closeExitModal = () => setShowExitModal(false);
  const openExitModal = () => setShowExitModal(true);

  const handleRemoved = async () => {
    logEvent('PROFILE_REMOVE_AFFILIATION_CLICKED', {
      title,
      ...(affiliationType === 'RINGGOLD'
        ? { organization_id: organizationId }
        : { organization_name: organization })
    });
    openExitModal();
  };
  const removeAffiliation = async () => {
    logEvent('PROFILE_REMOVE_AFFILIATION_REMOVE_AFFILIATION_CLICKED', {
      title,
      ...(affiliationType === 'RINGGOLD'
        ? { organization_id: organizationId }
        : { organization_name: organization })
    });
    await deleteAffiliation(id, type);
    if (isPrimaryAffiliation) {
      await updatePartialProfileGeneralInfo(userId, {
        primaryAffiliation: null
      });
      await mutateProfile();
    }
    await refetchAffiliations();
    closeExitModal();
  };
  const createdAtDiff = createdAt
    ? differenceInHours(new Date(), new Date(createdAt))
    : 0;
  const showResendEmailButton = createdAtDiff >= 72;

  const resendEmail = async () => {
    if (isLoading) {
      // if resending email process is already in process, do not do anything.
      return;
    }
    setIsLoading(true);
    try {
      logEvent('PROFILE_VALIDATE_AFFILIATION_RESEND_EMAIL_CLICKED', {
        affiliation_id: id,
        organization_id: organizationId
      });
      await resendAffiliationVerificationEmail(id, affiliationType);
      // after api success show this email sent confirmation popup
      setShowResendEmail(true);
    } catch (error) {
      // TODO: may be show error popup in future
      sentryCaptureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeResendEmailModal = () => {
    setShowResendEmail(false);
    setIsLoading(false);
    refetchAffiliations?.();
  };

  return (
    <div>
      <Modal
        ariaLabel="resend-affiliation-email"
        isOpen={showResendEmail}
        onDismiss={() => setShowResendEmail(false)}
      >
        <FormContainer showExitModal={false}>
          <EmailValidationView
            handleNavigaiton={() => {
              // added comment to avoid @typescript-eslint/no-empty-function error
            }}
            resetForm={() => {
              // added comment to avoid @typescript-eslint/no-empty-function error
            }}
            onDismiss={closeResendEmailModal}
            email={email}
            type={type}
            isEmailDomainMatchesOrgDomain={isEmailDomainMatchesOrgDomain!}
            showCTAButton={false}
          />
        </FormContainer>
      </Modal>
      <Flexbox gap="8px">
        <Title fontWeight="semiBold" textAs="span">
          {title}
        </Title>
        <AffiliationItemStatus
          type={type === 'RINGGOLD' ? 'RINGGOLD' : 'USER_PROVIDED'}
          ringgold={ringgold}
          validatedAt={validatedAt}
          isSameUser={isSameUser!}
          email={email}
        />
        {!validatedAt &&
          isSameUser &&
          isEmailDomainMatchesOrgDomain &&
          showResendEmailButton && (
            <LinkButton onClick={resendEmail}>
              {isLoading ? (
                <>
                  <Loader icon={faSpinnerThird} /> Sending email...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faEnvelope} /> Resend Email
                </>
              )}
            </LinkButton>
          )}
      </Flexbox>
      <Flexbox gap="8px">
        <SecondaryText fontWeight="medium">
          {format(new Date(startDate), 'MMM yyyy')} -{' '}
          {endDate ? format(new Date(endDate), 'MMM yyyy') : 'Present'}{' '}
          <span>&#183;</span> {location}
        </SecondaryText>
      </Flexbox>

      {department && (
        <Flexbox gap="8px">
          <Text>{department}</Text>
          {organizationType && (
            <SecondaryText fontWeight="medium">
              <span>&#183;</span> {getInstitutionTypeText(organizationType)}
            </SecondaryText>
          )}
        </Flexbox>
      )}
      <Flexbox gap="8px">
        {organization !== department && (
          <Flexbox gap="8px" alignItems="center">
            <Text>{organization}</Text>
            {!department && (
              <SecondaryText fontWeight="medium">
                {organization !== department && organizationType && (
                  <span>&#183;</span>
                )}{' '}
                {organizationType && getInstitutionTypeText(organizationType)}
              </SecondaryText>
            )}
          </Flexbox>
        )}
      </Flexbox>
      {isSameUser && (
        <Flexbox gap="16px" style={{ marginTop: '8px' }}>
          {editAffiliationDisabled ? (
            <DarkTooltip
              placement="bottom"
              text={MESSAGES.EDIT_AFFILIATIONS_DISABLED}
            >
              <LinkButton isDisable={editAffiliationDisabled} onClick={onClick}>
                <FontAwesomeIcon icon={faPencil} /> Edit
              </LinkButton>
            </DarkTooltip>
          ) : (
            <LinkButton onClick={onClick}>
              <FontAwesomeIcon icon={faPencil} /> Edit
            </LinkButton>
          )}
          <LinkButton onClick={handleRemoved}>
            <FontAwesomeIcon icon={faTrash} /> Remove
          </LinkButton>
        </Flexbox>
      )}
      <ExitModal
        onConfirm={removeAffiliation}
        onCancel={() => {
          logEvent('PROFILE_REMOVE_AFFILIATION_CANCEL_CLICKED', {
            title,
            ...(affiliationType === 'RINGGOLD'
              ? { organization_id: organizationId }
              : { organization_name: organization })
          });
          closeExitModal();
        }}
        isOpen={showExitModal}
        onClose={closeExitModal}
        title="Remove affiliation?"
        description="Your affiliation will be permanently removed from your profile."
        icon={faClose}
        confirmActionText="Remove affiliation"
        cancelActionText="Cancel"
      />
    </div>
  );
};

export default AffiliationItem;
